import React, { createContext, useContext, useState, useEffect } from 'react';

const SiteContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default theme (Margaret Núñez)
const defaultTheme = {
  primary_color: '#C4785A',
  secondary_color: '#F5F0E8',
  accent_color: '#F5E6C8',
  text_color: '#1c1917',
  font_heading: 'Playfair Display',
  font_body: 'Inter'
};

const defaultSite = {
  id: 'margaret-nunez',
  name: 'Margaret Núñez',
  slug: 'margaret-nunez',
  tagline: 'Experiences That People Remember',
  tagline_es: 'Experiencias Que la Gente Recuerda',
  owner_name: 'Margaret Núñez',
  owner_title: 'Founder / Creative Director',
  owner_title_es: 'Fundadora / Directora Creativa',
  theme: defaultTheme,
  social: {}
};

export const SiteProvider = ({ children }) => {
  const [site, setSite] = useState(defaultSite);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSiteConfig();
  }, []);

  useEffect(() => {
    if (site?.theme) {
      applyTheme(site.theme);
    }
  }, [site]);

  const fetchSiteConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/site/config`);
      if (response.ok) {
        const data = await response.json();
        setSite(data);
      }
    } catch (error) {
      console.error('Error fetching site config:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    // Apply CSS custom properties
    root.style.setProperty('--color-primary', theme.primary_color || defaultTheme.primary_color);
    root.style.setProperty('--color-secondary', theme.secondary_color || defaultTheme.secondary_color);
    root.style.setProperty('--color-accent', theme.accent_color || defaultTheme.accent_color);
    root.style.setProperty('--color-text', theme.text_color || defaultTheme.text_color);
    
    // Convert hex to RGB for Tailwind opacity support
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
    };
    
    root.style.setProperty('--color-primary-rgb', hexToRgb(theme.primary_color || defaultTheme.primary_color));
    root.style.setProperty('--color-secondary-rgb', hexToRgb(theme.secondary_color || defaultTheme.secondary_color));
    root.style.setProperty('--color-accent-rgb', hexToRgb(theme.accent_color || defaultTheme.accent_color));
    
    // Apply fonts
    if (theme.font_heading) {
      root.style.setProperty('--font-heading', theme.font_heading);
    }
    if (theme.font_body) {
      root.style.setProperty('--font-body', theme.font_body);
    }

    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme.primary_color || defaultTheme.primary_color);
    }
  };

  // Function to switch site (useful for multi-site preview)
  const switchSite = async (siteSlug) => {
    try {
      const response = await fetch(`${API_URL}/api/site/config`, {
        headers: {
          'X-Site-Id': siteSlug
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSite(data);
      }
    } catch (error) {
      console.error('Error switching site:', error);
    }
  };

  return (
    <SiteContext.Provider value={{ site, loading, switchSite, theme: site?.theme || defaultTheme }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export default SiteContext;
